<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';

const NXS_KEY = 'nxs_mail_r4k7p2w9x1m3';
const NXS_API = '/mail-api/nxs';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');

const emailMode = ref('none');
const existingAccounts = ref([]);
const selectedAccountId = ref('');
const emailDomains = ref([]);
const newEmailLocal = ref('');
const newEmailDomain = ref('');
const newEmailDisplayName = ref('');
const loadingAccounts = ref(false);

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const availableAccounts = computed(() => existingAccounts.value);

function nxsHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-NXS-Key': NXS_KEY,
    'X-NXS-Chatwoot-ID': String(currentUserId.value || ''),
    'X-NXS-Chatwoot-Name': currentUser.value?.name || 'Admin',
  };
}

async function loadEmailData() {
  loadingAccounts.value = true;
  try {
    const [accRes, domRes] = await Promise.all([
      fetch(NXS_API, {
        method: 'POST',
        headers: nxsHeaders(),
        body: JSON.stringify({ action: 'admin_list_all_accounts' }),
      }),
      fetch(NXS_API, {
        method: 'POST',
        headers: nxsHeaders(),
        body: JSON.stringify({ action: 'list_domains' }),
      }),
    ]);
    const accData = await accRes.json();
    const domData = await domRes.json();
    if (Array.isArray(accData)) existingAccounts.value = accData;
    if (Array.isArray(domData)) emailDomains.value = domData;
  } catch { /* ignore */ }
  loadingAccounts.value = false;
}

onMounted(() => { loadEmailData(); });

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    const newAgent = await store.dispatch('agents/create', payload);
    const agentId = newAgent?.id;

    if (agentId && emailMode.value !== 'none') {
      try {
        if (emailMode.value === 'link' && selectedAccountId.value) {
          await fetch(NXS_API, {
            method: 'POST',
            headers: nxsHeaders(),
            body: JSON.stringify({
              action: 'admin_link_account',
              account_id: selectedAccountId.value,
              target_chatwoot_id: agentId,
              target_name: agentName.value,
            }),
          });
        } else if (emailMode.value === 'create' && newEmailLocal.value && newEmailDomain.value) {
          await fetch(NXS_API, {
            method: 'POST',
            headers: nxsHeaders(),
            body: JSON.stringify({
              action: 'admin_create_account_for',
              localPart: newEmailLocal.value,
              domainId: newEmailDomain.value,
              name: newEmailDisplayName.value || agentName.value,
              target_chatwoot_id: agentId,
              target_name: agentName.value,
            }),
          });
        }
      } catch { /* email setup optional */ }
    }

    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <!-- Email Account Section -->
      <div class="w-full nxs-email-section">
        <div class="nxs-email-header">
          <span class="i-lucide-mail nxs-email-icon" />
          <span class="nxs-email-title">Conta de Email (servidor)</span>
        </div>

        <div class="nxs-email-options">
          <label class="nxs-radio">
            <input v-model="emailMode" type="radio" value="none" />
            <span>Nenhuma</span>
          </label>
          <label class="nxs-radio">
            <input v-model="emailMode" type="radio" value="link" />
            <span>Vincular existente</span>
          </label>
          <label class="nxs-radio">
            <input v-model="emailMode" type="radio" value="create" />
            <span>Criar novo email</span>
          </label>
        </div>

        <!-- Link existing -->
        <div v-if="emailMode === 'link'" class="nxs-email-form">
          <label>
            Selecionar conta
            <select v-model="selectedAccountId" class="nxs-select">
              <option value="">Selecione...</option>
              <option
                v-for="acc in availableAccounts"
                :key="acc.id"
                :value="acc.id"
              >
                {{ acc.address }} {{ acc.name ? `(${acc.name})` : '' }}
              </option>
            </select>
          </label>
          <p v-if="availableAccounts.length === 0" class="nxs-hint">
            Nenhuma conta disponível para vincular.
          </p>
        </div>

        <!-- Create new -->
        <div v-if="emailMode === 'create'" class="nxs-email-form">
          <label>
            Nome de exibição
            <input
              v-model="newEmailDisplayName"
              type="text"
              placeholder="Nome que aparece no email"
            />
          </label>
          <label>
            Endereço de email
            <div class="nxs-email-addr-row">
              <input
                v-model="newEmailLocal"
                type="text"
                placeholder="usuario"
                class="nxs-input-local"
              />
              <span class="nxs-at">@</span>
              <select v-model="newEmailDomain" class="nxs-select-domain">
                <option value="">Domínio</option>
                <option
                  v-for="d in emailDomains"
                  :key="d.id"
                  :value="d.id"
                >
                  {{ d.name }}
                </option>
              </select>
            </div>
          </label>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.nxs-email-section {
  margin-top: 8px;
  padding: 14px 16px;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 10px;
  background: rgb(var(--slate-1));
}

.nxs-email-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.nxs-email-icon {
  font-size: 16px;
  color: rgb(var(--blue-9));
}

.nxs-email-title {
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--slate-12));
}

.nxs-email-options {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

.nxs-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  cursor: pointer;
}

.nxs-radio input[type="radio"] {
  margin: 0;
  accent-color: rgb(var(--blue-9));
}

.nxs-email-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.nxs-email-form label {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--slate-12));
}

.nxs-email-addr-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.nxs-input-local {
  flex: 1;
}

.nxs-at {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--slate-10));
}

.nxs-select-domain {
  flex: 1;
}

.nxs-hint {
  font-size: 12px;
  color: rgb(var(--slate-9));
  margin: 0;
}
</style>
